import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { Modal, Flex, Box, Image, Text, Card, BackgroundImage, Button, Anchor } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import { useMediaQuery } from '@mantine/hooks';
import ModalBG from '../../../assets/images/common/bg.svg';
import { IconExternalLink, IconPlayerSkipForward } from '@tabler/icons-react';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useCampaignsStore } from '@/stores/useCampaignsStore';
import { LinksService } from '@/services/LinksService';
import { notifications } from '@mantine/notifications';
import { CampaignsService } from '@/services/CampaignsService';
import { QRCodeService } from '@/services/QRCodeService';
import { BioLinkService } from '@/services/BioLinkService';
import { isTrialAccount } from '@/utils/CommonUtility';
import { DEFAULT_LOGOS } from '@/constants/Constants';
import { usePlanStore } from '@/stores/usePlanStore';

type Props = {
    opened: boolean;
    setOpened: Dispatch<SetStateAction<boolean>>;
    imageSource?: string;
};

export const WelcomeModal = ({
    opened,
    setOpened,
    imageSource = '/images/tools/youtube-keyword/limit.svg',
}: Props) => {
    const matches = useMediaQuery('(max-width: 768px)');
    const [defaultWorkspace] = useWorkspaceStore((state) => [state.defaultWorkspace]);
    const [campaigns, fetchCampaigns] = useCampaignsStore((state) => [state.campaigns, state.fetchCampaigns]);
    const [plan] = usePlanStore((state) => [state.plan]);
    // Define arrays for cards
    const cards = [
        {
            id: 1,
            title: '🎨 Sleek, Intuitive Dashboard',
            description: 'Navigate effortlessly through a user-friendly interface designed for productivity.',
            image: 'https://storage.googleapis.com/replug-assets/web_app/6d06501a-b78e-4ac4-83c2-57c51eba8c6a.jpg',
            imageHeight: 160,
        },
        {
            id: 2,
            title: '🔗 Enhanced Bio Links',
            description: 'Create stunning link pages with our revamped, ready-to-use templates.',
            image: 'https://storage.googleapis.com/replug-assets/web_app/ed7a4b96-3748-4db6-932a-f8633cc36cf2.jpg',
            imageHeight: 160,
        },
        {
            id: 3,
            title: '🏢 Powerful Workspaces',
            description: 'Formerly brands, our new workspaces provide dedicated environments to oversee project performance.',
            image: 'https://storage.googleapis.com/replug-assets/web_app/d4b62251-1103-474a-8bb3-8fcbe4c615c1.jpg',
            imageHeight: 140,
        },
        {
            id: 4,
            title: '📊 Advanced Analytics',
            description: 'Gain quick insights into key metrics, empowering you to make data-driven decisions.',
            image: 'https://storage.googleapis.com/replug-assets/web_app/9ed9ae76-c33d-4217-b014-e2665fc99db1.jpg',
            imageHeight: 140,
        },
    ];

    // Get the Replug logo URL
    const replugLogoUrl = DEFAULT_LOGOS.find(logo => logo.name === 'Replug')?.url || 'https://storage.googleapis.com/replug-assets/web_app/logo.svg';

    useEffect(() => {
        // Only run this when the modal is opened
        if (opened) {
            checkAndCreatePendingLinks();
        }
    }, [opened]);

    const checkAndCreatePendingLinks = async () => {
        try {
            const pendingLinkDataStr = localStorage.getItem('pending_link_creation');
            if (!pendingLinkDataStr) {
                return;
            }
            const pendingLinkData = JSON.parse(pendingLinkDataStr);
            const workspaceId = defaultWorkspace?._id;
            if (!workspaceId) {
                console.error('No workspace ID found for link creation');
                return;
            }
            // Check if campaigns are loaded, if not fetch them
            let availableCampaigns = campaigns;
            if (!availableCampaigns || availableCampaigns.length === 0) {
                try {
                    // First try to fetch campaigns using the store method
                    fetchCampaigns(workspaceId);
                    
                    // If the store method doesn't immediately update the campaigns array,
                    // fetch them directly using the service
                    const campaignsService = new CampaignsService();
                    const response = await campaignsService.getAll(workspaceId);
                    availableCampaigns = response;
                } catch (error) {
                    console.error('Error fetching campaigns:', error);
                }
            }

            // Find a suitable campaign for link creation
            const defaultCampaign = availableCampaigns && availableCampaigns.length > 0 ? 
                (availableCampaigns.find(campaign =>
                    !campaign.is_call_to_action_enabled || campaign.is_retargeting_enabled
                ) || availableCampaigns[0]) : null;
            
            if (!defaultCampaign) {
                notifications.show({
                    color: 'red',
                    message: 'Unable to create your link. No campaigns available.',
                });
                return;
            }

            // Create short link if present
            if (pendingLinkData.shortUrl) {
                const linkService = new LinksService();
                const linkResponse = await linkService.create(workspaceId, {
                    url: pendingLinkData.shortUrl,
                    campaign_id: defaultCampaign._id,
                });

                if (linkResponse.data.status) {
                    // Mark as created
                    pendingLinkData.shortUrl = "";
                    localStorage.setItem('pending_link_creation', JSON.stringify(pendingLinkData));
                    notifications.show({
                        color: 'green',
                        message: 'Your link has been created successfully!',
                    });
                }
            }

            // Create QR code if present
            if (pendingLinkData.qrcodeUrl) {
                try {
                    // Create QR code directly with source_type 'new'
                    const qrCodeService = new QRCodeService();
                    // QR code configuration with source_type 'new'
                    const qrCodeData = {
                        title: 'My QR Code',
                        description: 'QR Code created from landing page',
                        foreground_color: '#000000',
                        background_color: pendingLinkData.qrCodeParams?.background_color || '#FFFFFF',
                        campaign_id: defaultCampaign._id,
                        source_type: 'new', // Let backend create both link and QR code
                        url: pendingLinkData.qrcodeUrl, // Original URL
                        shape_style: pendingLinkData.qrCodeParams?.shape_style || '',
                        border_style: pendingLinkData.qrCodeParams?.border_style || 'square',
                        center_style: pendingLinkData.qrCodeParams?.center_style || 'square',
                        shape_color: pendingLinkData.qrCodeParams?.shape_color || '#000000',
                        border_color: pendingLinkData.qrCodeParams?.border_color || '#000000',
                        center_color: pendingLinkData.qrCodeParams?.center_color || '#000000',
                        frame_type: pendingLinkData.qrCodeParams?.frame_type || 'none',
                        frame_color: pendingLinkData.qrCodeParams?.frame_color || '#FFFFFF',
                        frame_text: '',
                        frame_text_color: pendingLinkData.qrCodeParams?.frame_text_color || '#000000',
                        logo_image: replugLogoUrl,
                        logo_margin: 0,
                    };
                    console.log("qrCodeData", qrCodeData);
                    
                    const qrCodeResponse = await qrCodeService.create(workspaceId, qrCodeData);
                    
                    if (qrCodeResponse.data.status) {
                        // Mark as created
                        pendingLinkData.qrcodeUrl = "";
                        localStorage.setItem('pending_link_creation', JSON.stringify(pendingLinkData));
                        notifications.show({
                            color: 'green',
                            message: 'Your QR code has been created successfully!',
                        });
                    } else {
                        notifications.show({
                            color: 'red',
                            message: 'Failed to create your QR code. Please try again later.',
                        });
                    }
                } catch (error) {
                    notifications.show({
                        color: 'red',
                        message: 'Failed to create your QR code. Please try again later.',
                    });
                }
            }

            // For bio links
            if (pendingLinkData.biolinkUrl) {
                try {
                    // Extract the short URL from the bio link URL
                    // Format is expected to be like: mylinkinb.io/abcdecfgh
                    const urlParts = pendingLinkData.biolinkUrl.split('/');
                    const shortUrl = urlParts[urlParts.length - 1];
                    
                    // Create a new bio link
                    const bioLinkService = new BioLinkService();
                    
                    // Prepare bio link data with minimal required fields
                    const bioLinkData = {
                        short_url: shortUrl,
                        short_url_lowercase: shortUrl.toLowerCase(),
                        profile_title: shortUrl, // Use the short URL as the title as requested
                        workspace_id: workspaceId,
                        from_frontend_register: true,
                        theme: {
                            background_color: '#FFFFFF',
                            color: '#333333',
                            button: {
                                background_color: '#003aa9',
                                color: '#F5F5DC',
                                variant: 'outline',
                                radius: 'md',
                                box_shadow: '',
                            },
                            background_image: null,
                            background_opacity: 0.5,
                            is_background_image_enabled: false,
                        },
                        blocks: [],
                    };
                    
                    const bioLinkResponse = await bioLinkService.create(workspaceId, bioLinkData);
                    
                    if (bioLinkResponse.data.status) {
                        // Check if there are other pending links to create
                        if (pendingLinkData.shortUrl || pendingLinkData.qrcodeUrl) {
                            // Clear this URL but keep the item for other links
                            pendingLinkData.biolinkUrl = "";
                            localStorage.setItem('pending_link_creation', JSON.stringify(pendingLinkData));
                        } else {
                            // No other links to create, remove the item completely
                            localStorage.removeItem('pending_link_creation');
                        }
                        
                        notifications.show({
                            color: 'green',
                            message: 'Your bio link has been created successfully!',
                        });
                    } else {
                        notifications.show({
                            color: 'red',
                            message: 'Failed to create your bio link. Please try again later.',
                        });
                    }
                } catch (error) {
                    notifications.show({
                        color: 'red',
                        message: 'Failed to create your bio link. Please try again later.',
                    });
                }
            }
            
            // If all links have been processed (either created or empty strings)
            if (!pendingLinkData.shortUrl && !pendingLinkData.qrcodeUrl && !pendingLinkData.biolinkUrl) {
                localStorage.removeItem('pending_link_creation');
            }
        } catch (error) {
            notifications.show({
                color: 'red',
                message: 'Failed to create your link. Please try again later.',
            });
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={() => setOpened(false)}
            centered
            radius={16}
            size="64rem"
            closeOnClickOutside={false}
            overlayProps={{
                opacity: 0.3,
                color: '#000000',
            }}
        >
            <BackgroundImage src={ModalBG}>
                <Box p={16}>
                    <Flex justify="center" direction="column" align="center">
                        <Text fw={600} mb={8} fz={24} pt={8}>
                            {!defaultWorkspace?.agency?.is_whitelabel_enabled ? 'Introducing Replug 2.0: Your Link Management Suite, Reimagined 🚀' : ' Your Link Management Suite, Reimagined 🚀'}
                        </Text>
                        <Text fw={600} mb={8} fz={20} pt={8}>
                            {!defaultWorkspace?.agency?.is_whitelabel_enabled ? "What's New in Replug 2.0?" : "What's New?"}
                        </Text>
                        <Carousel
                            my={24}
                            slideGap="lg"
                            align="center"
                            slideSize={matches ? '100%' : '45%'}
                            slidesToScroll={1}
                            loop

                            nextControlProps={{
                                style: {
                                    backgroundColor: '#003aa9',
                                    color: '#FFFFFF',
                                    height: '36px',
                                    width: '36px',
                                },
                            }}
                            previousControlProps={{
                                style: {
                                    backgroundColor: '#003aa9',
                                    color: '#FFFFFF',
                                    height: '36px',
                                    width: '36px',
                                },
                            }}
                        >
                            {cards.map((card) => (
                                <Carousel.Slide key={card.id}>
                                    <Card withBorder shadow="md" radius="md" style={{ maxHeight: '298px' }}>
                                        <Card.Section style={{ padding: '16px' }}>
                                            <Image
                                                src={card.image}
                                                height="auto"
                                                width="100%"
                                                fit="contain"
                                                alt=""
                                                style={{ objectFit: 'contain', maxHeight: `${card.imageHeight}px` }}
                                            />
                                        </Card.Section>
                                        <Text ta="center" fw={700} mt="md" px="lg">
                                            {card.title}
                                        </Text>
                                        <Text ta="center" px="lg">
                                            {card.description}
                                        </Text>
                                    </Card>
                                </Carousel.Slide>
                            ))}
                        </Carousel>
                        <Flex justify="center" my={24} align="center" direction="row" gap={12}>
                            <Button
                                onClick={() => setOpened(false)}
                                size="md"
                                fullWidth
                                variant="outline"
                            >
                                Skip and try <IconPlayerSkipForward style={{
                                    marginLeft: '5px',
                                }} size={16} />
                            </Button>
                            <Anchor target='_blank' rel="noreferrer" href='https://updates.replug.io/announcements'>
                                <Button
                                    variant="filled"
                                    size="md"
                                    fullWidth
                                >
                                    Read full announcement  <IconExternalLink style={{
                                        marginLeft: '5px',
                                    }} size={18} />
                                </Button>
                            </Anchor>
                        </Flex>
                    </Flex>
                </Box>
            </BackgroundImage>
        </Modal>
    );
};
function createStyles(arg0: (theme: any) => {
    control: {
        backgroundColor: string; // Set your desired blue color here
        color: string; // Set text/icon color if necessary
        '&:hover': { backgroundColor: string; };
    };
}) {
    throw new Error('Function not implemented.');
}
